import './ConfirmModal.scss';

import {CloseOutlined, ExclamationCircleFilled, QuestionCircleOutlined} from '@ant-design/icons';
import cn from 'classnames';
import React, {useEffect, useId, useRef} from 'react';

import AntButton from '../../../../components/AntButton/AntButton';
import * as localizationKeys from '../../../../consts/localization/localizationKeys';
import {CONFIRM_MODAL_TYPE} from '../../../../consts/modals/modalTypes';
import useIsMobileViewport from '../../../../hooks/useIsMobileViewport';
import {getLocalizedStrings} from '../../../../services/localization/localizationService';
import {hideModalAction} from '../../../../state/ducks/modal';
import {dispatch} from '../../../../state/store';
import helpers from '../../../../utils/helpers';

const ConfirmModal = (props) => {
    const {onOk, onCancel, confirmModalTitle} = props;
    const localizedStrings = getLocalizedStrings();
    const isMobile = useIsMobileViewport();
    const titleId = useId();
    const modalRef = useRef(null);

    useEffect(() => {
        if (!isMobile) return undefined;

        const dialog = modalRef.current?.closest('[role="dialog"]');

        dialog?.setAttribute('aria-labelledby', titleId);
        return () => {
            if (dialog?.getAttribute('aria-labelledby') === titleId) dialog.removeAttribute('aria-labelledby');
        };
    }, [isMobile, titleId]);

    const handleOk = async () => {
        await helpers.runFunction(onOk);
        dispatch(hideModalAction(CONFIRM_MODAL_TYPE));
    };

    const handleCancel = () => {
        helpers.runFunction(onCancel);
        dispatch(hideModalAction(CONFIRM_MODAL_TYPE));
    };

    const submitButtonText = localizedStrings[localizationKeys.CONFIRM_MODAL_BUTTON_TEXT_SUBMIT];
    const cancelButtonText = localizedStrings[localizationKeys.CONFIRM_MODAL_BUTTON_TEXT_CANCEL];
    const titleTextContent = confirmModalTitle || localizedStrings[localizationKeys.CONFIRM_MODAL_DEFAULT_TITLE_TEXT];

    if (isMobile) {
        return (
            <div ref={modalRef} className='mobile-confirm-dialog'>
                <div className='mobile-confirm-heading'>
                    <button
                        type='button'
                        className='mobile-confirm-close ant-modal-close'
                        onClick={handleCancel}
                        aria-label={localizedStrings[localizationKeys.CONFIRM_MODAL_CLOSE]}
                    >
                        <CloseOutlined aria-hidden />
                    </button>
                </div>
                <div className='mobile-confirm-body'>
                    <span className='mobile-confirm-icon' aria-hidden>
                        <QuestionCircleOutlined />
                    </span>
                    <h2 id={titleId}>{titleTextContent}</h2>
                </div>
                <div className='mobile-confirm-actions'>
                    <button type='button' className='mobile-confirm-cancel' onClick={handleCancel}>
                        {cancelButtonText}
                    </button>
                    <button type='button' className='mobile-confirm-submit' onClick={handleOk}>
                        {submitButtonText}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div ref={modalRef} className='confirm-modal-content flex-column m-auto px-[55px] py-5'>
            <div className={'w-full text-center'}>
                <ExclamationCircleFilled className={'text-[60px] text-orange-peel'} />
            </div>
            <p className={'confirm-modal-title text-center text-24 my-8 font-semibold'}>{titleTextContent}</p>
            <div>
                <div className={cn('confirm-modal-actions flex justify-between items-center')}>
                    <AntButton className='h-[50px]' onClick={handleCancel} cancelHover bordered>
                        {cancelButtonText}
                    </AntButton>

                    <AntButton className='h-[50px]' htmlType='submit' primary onClick={handleOk}>
                        {submitButtonText}
                    </AntButton>
                </div>
            </div>
        </div>
    );
};

export default React.memo(ConfirmModal);
